import { useAuth } from 'src/auth/AuthContext';
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputBase,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronRight,
  Search as SearchIcon,
  StorefrontOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PermissionRole } from '../../constants';
import { useLocations } from 'src/queries/locations';

const headingFont = "'Bricolage Grotesque', sans-serif";
const bodyFont = "'Instrument Sans', system-ui, sans-serif";

const typeLabel = (typeId: number) => (typeId === 1 ? 'Coffee Shop' : 'AnyPerks');

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,2.4fr) minmax(128px,1fr) 28px',
  gap: '16px',
  alignItems: 'center'
};

const RolePill: React.FC<{ role: PermissionRole }> = ({ role }) => {
  const isAdmin = role === PermissionRole.Admin;
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: 26,
        px: '10px',
        borderRadius: '99px',
        backgroundColor: isAdmin ? '#EAF6FC' : '#EEF2F5',
        color: isAdmin ? '#0E4F70' : '#5F6B76',
        fontFamily: bodyFont,
        fontWeight: 500,
        fontSize: 13,
        whiteSpace: 'nowrap',
        width: 'fit-content'
      }}
    >
      <Box
        component='span'
        sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }}
      />
      {isAdmin ? 'Admin' : 'Staff'}
    </Box>
  );
};

const LocationsPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = useAuth().id;
  const isSuperAdmin = useAuth().isSuperAdmin;
  const [search, setSearch] = useState('');

  const { data: merchantList = [], isPending, isError } = useLocations(userId);

  const locations = useMemo(() => {
    const sorted = [...merchantList].sort((a, b) => a.name.localeCompare(b.name));
    const query = search.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter((location) => location.name.toLowerCase().includes(query));
  }, [merchantList, search]);

  const canOpen = (role: PermissionRole) => isSuperAdmin || role === PermissionRole.Admin;

  // Page-level gate: the app already waited for the auth bootstrap, this waits
  // for the location list itself. `isPending` is the direct equivalent of the old
  // `loaded` flag — true only while there is no data yet, whether or not a
  // request is in flight — so the "No locations yet" empty state can never flash
  // first. Using `isFetching` here would reintroduce that flash on every
  // background refresh.
  const isLoading = isPending;
  const hasFailed = isError;
  const hasLocations = merchantList.length > 0;

  const addLocationButton = (label: string) => (
    <Button
      variant='contained'
      startIcon={<AddIcon />}
      onClick={() => navigate('/create-location')}
      sx={{
        height: 44,
        px: '18px',
        backgroundColor: '#1778A8',
        fontFamily: bodyFont,
        fontSize: 15,
        '&:hover': { backgroundColor: '#0E4F70' }
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        maxWidth: 1080,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '28px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography
            component='h1'
            sx={{
              fontFamily: headingFont,
              fontWeight: 700,
              fontSize: 36,
              lineHeight: 1.05,
              letterSpacing: '-.02em',
              color: '#17222C'
            }}
          >
            Locations
          </Typography>
          <Typography sx={{ fontFamily: bodyFont, fontSize: 15, color: '#5F6B76' }}>
            {isLoading
              ? 'Loading your locations…'
              : hasFailed
                ? 'We could not load your locations.'
                : hasLocations
                  ? `${merchantList.length} location${merchantList.length === 1 ? '' : 's'}`
                  : 'Add a location to start your loyalty program.'}
          </Typography>
        </Box>
        {!isLoading && !hasFailed && hasLocations && addLocationButton('Add location')}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #E6ECF0',
            borderRadius: '20px',
            p: '72px 32px',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <CircularProgress sx={{ color: '#1778A8' }} />
        </Box>
      ) : hasFailed ? (
        // A failed fetch also leaves the list empty, but it is not the same as
        // owning no locations — say so instead of inviting a duplicate.
        <Alert severity='error' sx={{ fontFamily: bodyFont, borderRadius: '12px' }}>
          An error occurred while fetching your locations
        </Alert>
      ) : !hasLocations ? (
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px dashed #CBD8E1',
            borderRadius: '20px',
            p: '72px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '18px'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '18px',
              backgroundColor: '#EAF6FC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <StorefrontOutlined sx={{ fontSize: 30, color: '#0E4F70' }} />
          </Box>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: 380 }}
          >
            <Typography
              sx={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 24,
                lineHeight: 1.15,
                letterSpacing: '-.01em',
                color: '#17222C'
              }}
            >
              No locations yet
            </Typography>
            <Typography
              sx={{ fontFamily: bodyFont, fontSize: 15, lineHeight: 1.45, color: '#5F6B76' }}
            >
              Create your first location to start rewarding your customers.
            </Typography>
          </Box>
          {addLocationButton('Create location')}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 360,
              height: 42,
              border: '1px solid #CBD8E1',
              borderRadius: '10px',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              px: '12px'
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: '#7C8893' }} />
            <InputBase
              placeholder='Search locations'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: 1,
                fontFamily: bodyFont,
                fontSize: 15,
                color: '#17222C',
                '& input::placeholder': { color: '#94A2AE', opacity: 1 }
              }}
            />
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #E6ECF0',
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                ...rowGrid,
                p: '12px 20px',
                borderBottom: '1px solid #E6ECF0',
                fontFamily: bodyFont,
                fontWeight: 500,
                fontSize: 12,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: '#7C8893'
              }}
            >
              <span>Location</span>
              <span>Your role</span>
              <span />
            </Box>
            {locations.length === 0 ? (
              <Typography
                sx={{ p: '24px 20px', fontFamily: bodyFont, fontSize: 15, color: '#5F6B76' }}
              >
                No locations match “{search.trim()}”.
              </Typography>
            ) : (
              locations.map((location) => {
                const openable = canOpen(location.userPermissionRole);
                return (
                  <Box
                    key={location.id}
                    onClick={
                      openable ? () => navigate(`/locations/${location.id}`) : undefined
                    }
                    sx={{
                      ...rowGrid,
                      p: '16px 20px',
                      borderBottom: '1px solid #EEF2F5',
                      cursor: openable ? 'pointer' : 'default',
                      '&:last-of-type': { borderBottom: 0 },
                      '&:hover': openable ? { backgroundColor: '#F9FBFC' } : undefined
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}
                    >
                      <Avatar
                        src={location.logoUrl}
                        alt={location.name}
                        variant='rounded'
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          backgroundColor: '#17222C',
                          fontFamily: headingFont,
                          fontWeight: 700,
                          fontSize: 15
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                          minWidth: 0
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: bodyFont,
                            fontWeight: 600,
                            fontSize: 15,
                            color: '#17222C'
                          }}
                        >
                          {location.name}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{ fontFamily: bodyFont, fontSize: 14, color: '#5F6B76' }}
                        >
                          {typeLabel(location.typeId)}
                        </Typography>
                      </Box>
                    </Box>
                    <RolePill role={location.userPermissionRole} />
                    {openable ? <ChevronRight sx={{ color: '#7C8893' }} /> : <span />}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LocationsPage;
